import React, { useState, useEffect, useCallback } from 'react';
import { savePicks, getPicks, getCurrentRace, getRaces, isRaceLocked, getDrivers, getQuestions, getRaceDrivers, getRaceWeekendSummary } from '../../api';
import DriverSelector from '../../components/DriverSelector';
import RaceSubmissions from '../../components/RaceSubmissions';
import { useUser } from '../../context/UserContext';
import { useAppAlert } from '../../components/AppAlerts';

export default function Picks() {
    const { currentUser } = useUser();
    const notify = useAppAlert();
    const [raceId, setRaceId] = useState('');
    const [raceName, setRaceName] = useState('');
    const [positions, setPositions] = useState(Array(7).fill(null));
    const [dnf, setDnf] = useState(null);
    const [driversMap, setDriversMap] = useState({});
    const [locked, setLocked] = useState(false);
    const [message, setMessage] = useState('');
    const [questions, setQuestions] = useState([]);
    const [bonusAnswers, setBonusAnswers] = useState({}); // { questionId: optionNumber }
    const [raceDrivers, setRaceDrivers] = useState([]);
    const [qualifyingResults, setQualifyingResults] = useState([]);

    const showAlert = (text, type = null) => {
        if (!text) return;
        setMessage(text);
        const lower = String(text).toLowerCase();
        const resolvedType = type || (/error|unable|failed|select|provide|please|invalid|locked|no user/.test(lower) ? 'error' : 'success');
        notify(text, resolvedType);
    };

    const buildDriversMap = (list) => {
        const map = {};
        (list || []).forEach((d) => {
            map[d.id] = (d.name || `${d.first_name || ''} ${d.last_name || ''}`).trim();
        });
        return map;
    };

    const getQualifyingDriverLabel = (row) => {
        if (!row) return 'â€”';
        return row.driverName || [row.first_name, row.last_name].filter(Boolean).join(' ').trim() || row.code || 'â€”';
    };

    const loadPicks = useCallback(async (rid, userId) => {
        try {
            const all = await getPicks(rid, userId);
            const me = all.find((p) => String(p.username) === String(userId));
            if (me) {
                const normalized = me.picks.slice(3, 10).map((v) => v ?? null);
                setPositions((prev) => [
                    ...normalized,
                    ...Array(Math.max(0, prev.length - normalized.length)).fill(null),
                ]);
                setDnf(me.dnf ?? null);
                // Parse bonus_answers string: "qId:answer,qId:answer"
                if (me.bonus_answers) {
                    const parsed = {};
                    me.bonus_answers.split(',').forEach((pair) => {
                        const [qId, ans] = pair.split(':');
                        if (qId && ans) parsed[qId] = parseInt(ans);
                    });
                    setBonusAnswers(parsed);
                } else {
                    setBonusAnswers({});
                }
            } else {
                setPositions(Array(7).fill(null));
                setDnf(null);
                setBonusAnswers({});
            }
        } catch (e) {
            console.error('Error loading picks', e);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const [driverList, currentRaceId] = await Promise.all([getDrivers(), getCurrentRace()]);

                if (cancelled) return;

                setDriversMap(buildDriversMap(driverList));

                if (!currentRaceId) return;
                setRaceId(currentRaceId);

                const [races, lockedState, qs, rdResp, weekendSummary] = await Promise.all([getRaces(), isRaceLocked(currentRaceId), getQuestions(currentRaceId), getRaceDrivers(currentRaceId), getRaceWeekendSummary(currentRaceId)]);
                if (cancelled) return;

                const race = races.find((r) => String(r.id) === String(currentRaceId));
                setRaceName(race?.name || '');
                setLocked(!!lockedState);
                setQuestions(qs || []);

                // Use race-specific drivers if available, else all drivers
                const rdList = rdResp?.drivers || [];
                if (rdList.length > 0) {
                    setRaceDrivers(rdList);
                    setDriversMap(buildDriversMap(rdList));
                } else {
                    setRaceDrivers(driverList);
                }

                setQualifyingResults(
                    Array.isArray(weekendSummary?.current?.qualifying)
                        ? [...weekendSummary.current.qualifying].sort((a, b) => (Number(a?.position) || 999) - (Number(b?.position) || 999))
                        : []
                );

                if (currentUser?.id) {
                    await loadPicks(currentRaceId, currentUser.id);
                }
            } catch (err) {
                console.error('Error loading picks page data', err);
                setQualifyingResults([]);
            }
        })();

        return () => { cancelled = true; };
    }, [currentUser, loadPicks]);

    function updatePos(index, value) {
        setPositions((prev) => prev.map((v, i) => (i === index ? value : v)));
    }

    async function handleSubmit(e) {
        if (e?.preventDefault) e.preventDefault();
        if (!currentUser) return showAlert('No user selected');
        if (locked) return showAlert('Picks are locked for this race');
        if (positions.some((p) => p == null)) return showAlert('Please fill all pick positions before saving');

        // Serialize bonus answers: "qId:answer,qId:answer"
        const bonusStr = Object.entries(bonusAnswers)
            .filter(([, v]) => v != null)
            .map(([qId, ans]) => `${qId}:${ans}`)
            .join(',') || null;

        const useRace = raceId || (await getCurrentRace());
        try {
            await savePicks(currentUser.id, [null, null, null, ...positions], useRace, dnf, bonusStr);
            showAlert('Picks saved for ' + (raceName || useRace));
            const nowLocked = await isRaceLocked(useRace);
            setLocked(!!nowLocked);
        } catch (err) {
            showAlert(err?.message || 'Unable to save picks');
            const nowLocked = await isRaceLocked(useRace);
            setLocked(!!nowLocked);
        }
    }

    const lockedDisplay = (val, fallback = '') => driversMap[val] || fallback;
    const getBonusAnswerLabel = (q, selected) => {
        const options = [
            q.option_1 ? { label: q.option_1, value: 1 } : null,
            q.option_2 ? { label: q.option_2, value: 2 } : null,
            q.option_3 ? { label: q.option_3, value: 3 } : null,
            q.option_4 ? { label: q.option_4, value: 4 } : null,
        ].filter(Boolean);
        return options.find((o) => o.value === selected)?.label || 'No answer';
    };

    return (
        <>
            <form onSubmit={(e) => e.preventDefault()} className="mb-3">
                <div className="mb-3">
                    <label className="form-label">Enter Picks</label>
                    {locked ? (
                        <div className="locked-picks-compact">
                            <div className="row g-2">
                                {positions.map((val, i) => (
                                    <div key={`locked-pos-${i}`} className="col-12 col-md-6">
                                        <div className="locked-pick-item d-flex justify-content-between align-items-center">
                                            <span className="locked-pick-label">#{i + 4}</span>
                                            <span className="locked-pick-value">{lockedDisplay(val, 'â€”')}</span>
                                        </div>
                                    </div>
                                ))}
                                <div className="col-12 col-md-6">
                                    <div className="locked-pick-item d-flex justify-content-between align-items-center">
                                        <span className="locked-pick-label">DNF</span>
                                        <span className="locked-pick-value">{lockedDisplay(dnf, 'No DNF')}</span>
                                    </div>
                                </div>
                            </div>

                            {questions.length > 0 && (
                                <div className="mt-3">
                                    <label className="form-label fw-semibold mb-2">Bonus Questions</label>
                                    <div className="row g-2">
                                        {questions.map((q, idx) => (
                                            <div key={`locked-q-${q.id}`} className="col-12 col-md-6">
                                                <div className="locked-pick-item d-flex justify-content-between align-items-center">
                                                    <span className="locked-pick-label">Q{idx + 1}</span>
                                                    <span className="locked-pick-value">{getBonusAnswerLabel(q, bonusAnswers[String(q.id)])}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="form-group row">
                            {positions.map((val, i) => (
                                <div key={`pos-${i}`} className="mb-3 row align-items-center">
                                    <label className="col-1 col-form-label">{`#${i + 4}`}</label>
                                    <div className="col-11">
                                        <DriverSelector value={val} onChange={(v) => updatePos(i, v)} drivers={raceDrivers.length > 0 ? raceDrivers : undefined} />
                                    </div>
                                </div>
                            ))}
                            <div className="mb-3 row align-items-center">
                                <label className="col-1 col-form-label">DNF</label>
                                <div className="col-11">
                                    <DriverSelector value={dnf} onChange={(v) => setDnf(v)} includeNoDnf={true} drivers={raceDrivers.length > 0 ? raceDrivers : undefined} />
                                </div>
                            </div>

                            {/* Bonus Questions */}
                            {questions.length > 0 && (
                                <>
                                    <hr />
                                    <label className="form-label fw-semibold">Bonus Questions</label>
                                    {questions.map((q, idx) => {
                                        const options = [
                                            q.option_1 ? { label: q.option_1, value: 1 } : null,
                                            q.option_2 ? { label: q.option_2, value: 2 } : null,
                                            q.option_3 ? { label: q.option_3, value: 3 } : null,
                                            q.option_4 ? { label: q.option_4, value: 4 } : null,
                                        ].filter(Boolean);
                                        const selected = bonusAnswers[String(q.id)];
                                        return (
                                            <div key={q.id} className="mb-3 row align-items-start">
                                                <label className="col-1 col-form-label">Q{idx + 1}</label>
                                                <div className="col-11">
                                                    <div className="mb-1">
                                                        <strong>{q.question_text}</strong>
                                                        <span className="badge bg-secondary ms-2">{q.points} pt{q.points !== 1 ? 's' : ''}</span>
                                                    </div>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {options.map((opt) => (
                                                            <div key={opt.value} className="form-check">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="radio"
                                                                    name={`bonus-q-${q.id}`}
                                                                    id={`bonus-q-${q.id}-opt-${opt.value}`}
                                                                    checked={selected === opt.value}
                                                                    onChange={() => setBonusAnswers((prev) => ({ ...prev, [String(q.id)]: opt.value }))}
                                                                />
                                                                <label className="form-check-label" htmlFor={`bonus-q-${q.id}-opt-${opt.value}`}>
                                                                    {opt.label}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    )}
                </div>

                <button className="btn btn-primary mt-3" type="button" onClick={handleSubmit} disabled={locked}>
                    Save Picks
                </button>
            </form>

            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="mb-3">Qualifying Results</h5>
                    {qualifyingResults.length > 0 ? (
                        <ol className="qualifying-results-list mb-0">
                            {qualifyingResults.map((row, index) => (
                                <li key={`${row.position ?? index}-${getQualifyingDriverLabel(row)}`} value={row.position ?? index + 1}>
                                    {getQualifyingDriverLabel(row)}
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <div className="text-muted small">No qualifying results available yet.</div>
                    )}
                </div>
            </div>

            <RaceSubmissions raceId={raceId} />
        </>
    );
}

